import { useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { Eye, PencilLine, FileText } from 'lucide-react';
import { useSiteQuery } from '../features/site/hooks.ts';
import { siteNotProvisioned } from '../features/site/api.ts';
import { SectionPanel } from '../features/site/SectionPanel.tsx';
import { ServicesEditor } from '../features/site/ServicesEditor.tsx';
import { PreviewPane } from '../features/site/PreviewPane.tsx';
import { SiteTopBar } from '../features/site/SiteTopBar.tsx';
import type { SiteBundle } from '../features/site/api.ts';

function gapsFor(bundle: SiteBundle, group: string): number {
  return bundle.complianceGaps.filter((g) => g.group === group).length;
}

function ContentInner({ bundle, initial }: { bundle: SiteBundle; initial?: string }): JSX.Element {
  // Only mandated groups have editable content fields.
  const groups = bundle.groups.filter((g) => g.mandated);
  const [sel, setSel] = useState<string>(() => (initial && groups.some((g) => g.group === initial) ? initial : groups[0]?.group ?? ''));
  const [mobile, setMobile] = useState<'edit' | 'preview'>('edit');

  const active = groups.find((g) => g.group === sel);

  return (
    <div className="flex h-[calc(100vh-49px)] min-h-0">
      {/* Left rail — pages */}
      <aside className={`w-60 shrink-0 overflow-y-auto border-e border-border p-2 ${mobile === 'preview' ? 'hidden xl:block' : ''}`}>
        <p className="px-2 pb-1.5 pt-1 text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground">Pages</p>
        <ul className="space-y-0.5">
          {groups.map((g) => {
            const complete = gapsFor(bundle, g.group) === 0;
            return (
              <li key={g.group}>
                <button
                  type="button"
                  onClick={() => setSel(g.group)}
                  className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-start text-sm font-medium transition-colors ${sel === g.group ? 'bg-primary/10 text-primary' : 'text-foreground/80 hover:bg-muted'}`}
                >
                  <span className="truncate">{g.titleEn}</span>
                  <span aria-hidden="true" title={complete ? 'Complete' : 'Needs content'} className={`ms-auto h-1.5 w-1.5 shrink-0 rounded-full ${complete ? 'bg-[hsl(var(--success))]' : 'bg-destructive/60'}`} />
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      {/* Middle — content form */}
      <div className={`min-w-0 flex-1 overflow-y-auto p-5 xl:max-w-2xl ${mobile === 'preview' ? 'hidden xl:block' : ''}`}>
        {active ? (
          active.group === 'services'
            ? <ServicesEditor key="services" bundle={bundle} />
            : <SectionPanel key={active.group} group={active} documents={bundle.documents} />
        ) : (
          <div className="grid h-full place-items-center text-sm text-muted-foreground"><FileText className="me-2" /> Select a page</div>
        )}
      </div>

      {/* Preview */}
      <div className={`min-w-0 flex-1 border-s border-border ${mobile === 'edit' ? 'hidden xl:block' : ''}`}>
        <PreviewPane path={sel} />
      </div>

      {/* Mobile toggle */}
      <div className="fixed bottom-4 left-1/2 z-30 -translate-x-1/2 xl:hidden">
        <div className="inline-flex rounded-full border border-border bg-paper p-1 shadow-lg">
          <button type="button" onClick={() => setMobile('edit')} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ${mobile === 'edit' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}><PencilLine size={14} /> Edit</button>
          <button type="button" onClick={() => setMobile('preview')} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ${mobile === 'preview' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}><Eye size={14} /> Preview</button>
        </div>
      </div>
    </div>
  );
}

export function Content(): JSX.Element {
  const { data: bundle, isLoading, error } = useSiteQuery();
  const search = useSearch({ strict: false }) as { s?: string };

  if (isLoading) return <div className="page"><p role="status" className="status-label">Loading…</p></div>;
  if (error && siteNotProvisioned(error)) {
    return (
      <div className="page">
        <div className="card-paper mx-auto mt-10 max-w-lg space-y-3 p-8 text-center">
          <h1 className="font-serif text-2xl text-ink">Your council site hasn't been set up yet</h1>
          <p className="text-sm text-muted-foreground">Once your WapSite is provisioned you'll fill in each page's content here.</p>
        </div>
      </div>
    );
  }
  if (error || !bundle) {
    return (
      <div className="page">
        <div role="alert" className="max-w-lg rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Something went wrong loading your site. Please refresh and try again.
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteTopBar bundle={bundle} tool="content" />
      <ContentInner bundle={bundle} initial={search.s} />
    </div>
  );
}
